import React, { useState, useContext, useMemo } from "react";
import { matchStaffRecord, calcLoanDeductions, staffViewCan, getAllUserProfiles } from "../../permissions/checks";
import StaffHeader from "../../shared/StaffHeader";
import PlatformCtx from "../../state/PlatformCtx";

import StaffBottomNav from "./StaffBottomNav";
import TasksPanel from "./panels/Tasks";
import { GroceryRequestPanel } from "./panels/Groceries";
import ExpensesPanel from "./panels/Expenses";
import LeavePanel from "./panels/Leave";
import InfoPanel from "./panels/Info";
import SuppliersPanel from "./panels/Suppliers";

/* ══ UNIFIED STAFF WORKSPACE ═══════════════════════════════
   Visual parity with the main workspace:
     – .staff-wrap wraps the whole view; .staff-hdr shown on desktop,
       hidden on mobile by CSS (@media ≤768px).
     – .page container hosts the .ph page header + .tabs strip +
       active panel body, mirroring the main workspace layout.
     – .tabs (desktop) and StaffBottomNav (mobile) are two renderings
       of the same capability-filtered tab list, driven by one manifest.

   The manifest's `group` field maps each tab to a StaffBottomNav
   group (today / requests / directory). See StaffBottomNav for details.
*/
function StaffView({state,dispatch,user,onLogout,t,switchLang,lang}){
  const platformCtx=useContext(PlatformCtx);
  const isRTL=lang==="ar";
  const {locations,staff,notifications}=state;

  /* Staff record / loans / salary — derived once, threaded to panels */
  const myStaffRecord=useMemo(()=>matchStaffRecord(staff,user),[staff,user]);
  const myLoans=myStaffRecord?.loans||[];
  const activeLoanDeductions=calcLoanDeductions(myLoans);
  const netSalary=myStaffRecord?(myStaffRecord.salary-activeLoanDeductions):null;

  /* Merged lookup — covers both platform profiles and workspace-defined Staff roles */
  const userProfile=getAllUserProfiles(state,platformCtx).find(p=>p.id===user.role);
  const roleLabel=userProfile?(isRTL&&userProfile.nameAr?userProfile.nameAr:userProfile.name):user.role;
  const locLabel=user.assignedLoc?((l)=>isRTL&&l?.nameAr?l.nameAr:l?.name)(locations.find(l=>l.id===user.assignedLoc)):null;

  const panelCtx={state,dispatch,user,isRTL,lang,t,platformCtx,myStaffRecord,myLoans,activeLoanDeductions,netSalary};

  /* Derived counts from the Task Engine + other sources for bottom-nav badges */
  const myTaskCount =(state.tasks||[]).filter(tk=>tk.assignedTo===user.id&&!tk.deletedAt&&tk.status!=="completed"&&tk.status!=="cancelled").length;
  const leaveBadge  =(state.leaveRequests||[]).filter(r=>r.staffId===myStaffRecord?.id&&r.status==="pending").length;

  /* Tab manifest — declarative source of truth for desktop tabs AND the
     bottom-nav groups. `group` drives which StaffBottomNav primary opens
     this tab's arc. `capability` is either a boolean (pre-evaluated) or
     a key checked against staffViewCan(). */
  /* Two content groups: work (operational, action-oriented) and me
     (personal records + self-service). Language + Logout are direct
     actions on the bottom nav — not part of any group manifest. */
  const manifest=[
    /* ── WORK ── */
    {key:"tasks",      group:"work", label:isRTL?"المهام":"Tasks",                   capability:"viewOwnTasks",         badge:myTaskCount, fullBleed:true, hasFab:true,
      render:()=><TasksPanel {...panelCtx}/>},
    /* Rides tab removed — ride tasks appear in Tasks with module-aware
       row subtitles (pickup → dropoff · time) and the full Transport
       context in the task detail modal. Day grouping surfaces Today /
       Tomorrow / Later the way the dedicated Rides panel used to. */
    {key:"groceryReq", group:"work", label:isRTL?"طلبات البقالة":"Grocery Requests", capability:"requestGroceries",
      render:()=><GroceryRequestPanel {...panelCtx}/>},
    {key:"expenses",   group:"work", label:isRTL?"المصاريف":"Expenses",              capability:"logOwnExpenses",       fullBleed:true, hasFab:true,
      render:()=><ExpensesPanel {...panelCtx}/>},
    {key:"suppliers",  group:"work", label:isRTL?"الموردون":"Suppliers",             capability:"viewStaffSuppliers",   fullBleed:true,
      render:()=><SuppliersPanel {...panelCtx}/>},
    /* ── ME ── */
    {key:"info",       group:"me",   label:isRTL?"ماليتي":"My Finances",             capability:"viewOwnProfile",       fullBleed:true, hasFab:true,
      render:()=><InfoPanel {...panelCtx}/>},
    /* History panel removed — past grocery submissions surface as tasks
       in the Tasks tab (linked via taskId), and status changes there
       cascade to linked grocery items. One less tab, same visibility. */
    {key:"leave",      group:"me",   label:isRTL?"الإجازات":"My Leaves",             capability:"requestOwnLeave",      badge:leaveBadge, fullBleed:true, hasFab:true,
      render:()=><LeavePanel {...panelCtx}/>},
  ];

  const tabs=manifest.filter(tb=>{
    if(typeof tb.capability==="boolean")return tb.capability;
    return staffViewCan(tb.capability,user,state,platformCtx);
  });

  const [activeKey,setActiveKey]=useState(tabs[0]?.key||"info");
  const activeTab=tabs.find(tb=>tb.key===activeKey)||tabs[0];

  /* Desktop tab strip — rendered outside the per-panel container so it stays
     available whether the active panel is fullBleed (provides its own .page
     + hero, like TaskEnginePanel) or standard (inherits StaffView's .page
     + .ph identity header). Hidden on mobile via CSS — StaffBottomNav is
     the primary nav there. */
  const tabStrip=(
    <div className="tabs staff-tabs" style={{overflowX:"auto",whiteSpace:"nowrap",padding:"0 24px",marginBottom:0}}>
      {tabs.map(tb=>(
        <div key={tb.key} className={`tab ${activeKey===tb.key?"active":""}`} onClick={()=>setActiveKey(tb.key)} style={{display:"inline-flex",alignItems:"center",gap:6}}>
          {tb.label}
          {tb.badge>0&&<span className="pill p-high" style={{fontSize:9,padding:"1px 6px"}}>{tb.badge}</span>}
        </div>
      ))}
    </div>
  );

  return(
    <div className={`staff-wrap ${isRTL?"rtl":""}`} style={{fontFamily:isRTL?"var(--fa)":"var(--fi)"}}>
      <StaffHeader user={user} lang={lang} switchLang={switchLang} onLogout={onLogout} dispatch={dispatch} notifications={notifications||[]} t={t} appName={state.appName}/>
      {tabStrip}
      {/* Panels tagged `fullBleed` render their own top chrome (e.g. Tasks
          reuses TaskEnginePanel which has its own .stf-hero + .page
          container). For those we skip StaffView's .ph + .page so there's
          no double padding or double title. Standard panels render inside
          a shared .ph identity header. */}
      {activeTab?.fullBleed?(
        activeTab?activeTab.render():null
      ):(
        <div className="page">
          {/* Identity header — avatar + active panel title + role subtitle */}
          <div className="ph">
            <div style={{display:"flex",alignItems:"center",gap:12,minWidth:0}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:"var(--ink)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color:"white",flexShrink:0}}>{user.initials}</div>
              <div style={{minWidth:0}}>
                <div className="ptitle" style={{lineHeight:1.1}}>{activeTab?.label||(isRTL?"مساحة الموظف":"Staff")}</div>
                <div className="psub" style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {isRTL&&user.nameAr?user.nameAr:user.name} · {roleLabel}{locLabel?` · ${locLabel}`:""}
                </div>
              </div>
            </div>
          </div>

          {activeTab?activeTab.render():<div className="empty">{isRTL?"لا صلاحيات متاحة. تواصل مع المسؤول.":"No staff panels available. Contact your administrator."}</div>}
        </div>
      )}

      {/* Mobile bottom nav — group-based, mirrors the main workspace pattern */}
      <StaffBottomNav
        tabs={tabs}
        activeKey={activeKey}
        setActiveKey={setActiveKey}
        lang={lang}
        onSwitchLang={switchLang}
        onLogout={onLogout}
        isRTL={isRTL}
      />
    </div>
  );
}

export default StaffView;
